import { Component, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { NavigationService } from '../../../../core/services/navigation.service';

@Component({
  selector: 'app-admin-sidebar',
  imports: [CommonModule, RouterLink, RouterLinkActive, MatListModule, MatIconModule, MatTooltipModule, MatButtonModule],
  templateUrl: './admin-sidebar.html',
  styleUrl: './admin-sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSidebarComponent {
  private navService = inject(NavigationService);
  
  // Local hover state
  readonly isHovered = signal<boolean>(false);
  
  // Fixed state from service
  readonly isSidebarCollapsed = this.navService.isSidebarCollapsed;

  // Visual expanded state: expanded if NOT fixed-collapsed OR if hovered
  readonly isExpanded = computed(() => !this.isSidebarCollapsed() || this.isHovered());

  menuItems = this.navService.menuItems;
  readonly expandedMenus = signal<Set<string>>(new Set());

  toggleSubmenu(label: string, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    const current = new Set(this.expandedMenus());
    if (current.has(label)) {
      current.delete(label);
    } else {
      current.add(label);
    }
    this.expandedMenus.set(current);
  }

  isMenuExpanded(label: string): boolean {
    return this.expandedMenus().has(label);
  }

  /**
   * @description Toggle the fixed sidebar state.
   */
  toggleSidebar(): void {
    this.navService.toggleSidebar();
  }

  /**
   * @description Handle mouse enter for hover expansion.
   */
  onMouseEnter(): void {
    if (this.isSidebarCollapsed()) {
      this.isHovered.set(true);
    }
  }

  /**
   * @description Handle mouse leave to return to collapsed state.
   */
  onMouseLeave(): void {
    this.isHovered.set(false);
  }
}
